import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Anchor,
  Center,
  SimpleGrid,
  Stack,
  Text,
  Title,
  type NotificationProps,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck } from "@tabler/icons-react";
import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type IntlShape,
} from "react-intl";
import { AppWebviewLayout } from "../AppWebviewLayout";
import { LoaderView } from "../LoaderView";
import shared from "../appWebviewShared.module.css";
import { useUpdateCustomerData } from "../../../hooks/ui";
import { isFullCredentials } from "../../../hooks/ui/customerDataStore";
import { useGetCustomerInfoById } from "../../../hooks/api/useGetCustomer";
import { useGetCalculationById } from "../../../hooks/api/useGetCalculationById";
import {
  isPoaBlocked,
  useGetPoaStatus,
} from "../../../hooks/api/useGetPoaStatus";
import { usePatchCustomerSalary } from "../../../hooks/api/usePatchCustomerSalary";
import { orgKey, summarizeCollection } from "../../../utils/pensionCollection";
import { buildSegments } from "./pensionSegments";
import { PensionDistributionBar } from "./PensionDistributionBar";
import { PensionFigures } from "./PensionFigures";
import { ProviderList, type ProviderRowData } from "./ProviderList";
import { ActionCarousel } from "./ActionCarousel";
import {
  CollectionProgressBanner,
  useDismissed,
  type ProgressItem,
} from "./CollectionProgressBanner";
import {
  ForecastBasisSheet,
  IncomeSheet,
  PoaDetailsSheet,
  SamblaHandoffSheet,
} from "./sheets";
import { DataCollectionScreen } from "./DataCollectionScreen";
import { InactivePoaBanner } from "./InactivePoaBanner";
import { NoPensionFoundScreen } from "../poa/NoPensionFoundScreen";
import { buildActionCards } from "./actionCards";
import { decideNextStep } from "./advisoryDecision";
import { ContactDrawer } from "../ContactDrawer";
import { askSam } from "../askSam";
import { usePatchCustomerAppData } from "../../../hooks/api/usePatchCustomerAppData";
import { normalizeToE164 } from "../../../utils/contactDetailsValidation";
import { trackEvent } from "../../../utils/statsig";

type OpenSheet = "income" | "forecast" | "poa" | "handoff" | null;

export interface AppPensionOverviewProps {
  /** The native app reports whether the Sam chat is available in this build. */
  isChatAvailable?: boolean;
  /** Starts a new POA signing when the current one is no longer valid. */
  onReactivate?: () => void;
}

const PROGRESS_MESSAGES = defineMessages({
  done: {
    id: "app.poaOverview.progressBanner.item.done",
    defaultMessage: "{source}: tillgänglig nu",
  },
  pending: {
    id: "app.poaOverview.progressBanner.item.pending",
    defaultMessage: "{source}: hämtas fortfarande",
  },
});

// The check and the spinner are decorative, so the state has to live in the label
// itself or a screen reader hears two identical rows.
const progressItem = (
  intl: IntlShape,
  source: string,
  done: boolean,
): ProgressItem => ({
  label: intl.formatMessage(
    done ? PROGRESS_MESSAGES.done : PROGRESS_MESSAGES.pending,
    { source },
  ),
  done,
});

const TOAST_STYLES: NotificationProps["styles"] = {
  root: {
    padding: "var(--mantine-spacing-md)",
    boxShadow: "0 4px 16px 0 rgba(0, 0, 0, 0.15)",
  },
  icon: { width: 20, height: 20 },
};

const isPositive = (value: number | null | undefined) =>
  typeof value === "number" && value > 0;

export function AppPensionOverview({
  isChatAvailable = false,
  onReactivate,
}: AppPensionOverviewProps) {
  const intl = useIntl();
  const { customerData } = useUpdateCustomerData();
  const {
    credentials,
    firstName,
    lastName,
    personalNumber,
    email,
    phoneNumber,
  } = customerData;
  const enabled = isFullCredentials(credentials);

  const [sheet, setSheet] = useState<OpenSheet>(null);
  const closeSheet = () => setSheet(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [advisoryRequestSent, setAdvisoryRequestSent] = useState(false);
  // Polling is decided by the summary that the poll itself feeds, so the flag is
  // state rather than a value derived further down this render.
  const [isCollecting, setIsCollecting] = useState(true);

  const customerQuery = useGetCustomerInfoById({ credentials, enabled });
  const customer = customerQuery.data?.[0];
  // A request sent in an earlier session is stamped on the app data.
  const advisoryRequested =
    advisoryRequestSent || Boolean(customer?.appData?.engagement?.ctaClickedAt);
  const salary = Number(customer?.user?.salary);

  // APP_OVERVIEW is the app's own response type: the same current-state figures plus
  // the forecast basis and the fee total, without the advice calculations the app
  // never shows.
  const calculationQuery = useGetCalculationById({
    credentials,
    enabled,
    responseType: "APP_OVERVIEW",
    // The POA status says a company answered; only this call carries the money that
    // answer produced, so both have to poll or the overview never fills in.
    refetchInterval: isCollecting ? 30_000 : false,
  });
  const poaQuery = useGetPoaStatus({ credentials, enabled, isCollecting });

  const calculation = calculationQuery.data;
  const distribution = calculation?.paymentDistribution;
  const insurances = calculation?.totalInsuranceList ?? [];
  const forecast = calculation?.forecast;
  // The backend is the authority on whether it could use an income; the stored salary
  // is the fallback for the moment before the first calculation lands.
  const hasIncome = forecast
    ? !forecast.missingIncome
    : Number.isFinite(salary) && salary > 0;

  const poas = poaQuery.data?.poas ?? [];
  // A first POA waits for a stored public balance (sumOfAssets less the holdings): the
  // projected shares are positive for everyone. An ended POA keeps the shares as before.
  const allocation = calculation?.totalAllocation;
  const hasPublicPensionData = poas.some((poa) => !poa.isValid)
    ? isPositive(distribution?.PPM_Percentage) ||
      isPositive(distribution?.I_Percentage) ||
      isPositive(distribution?.G_Percentage)
    : Number(allocation?.sumOfAssets) >
      Number(allocation?.sumOfInsuranceAssets);

  // The backend lists valid POAs first, then newest first, so the head is the
  // one whose reason explains why nothing is being collected.
  const latestPoa = poas[0];
  const poaInactive = poas.length > 0 && !poas.some((poa) => poa.isValid);
  const blocked = isPoaBlocked(poaQuery.data?.blockedUntil);
  // No company reported a pension. The screen is the same during and after the
  // block; only the retry CTA appears once the block has passed.
  const noPensionData =
    poaInactive &&
    (blocked || latestPoa?.inactivatedReason === "NO_PENSION_DATA");

  const collection = summarizeCollection({
    poaStatus: poas,
    hasPublicPensionData,
    hasAnyInsuranceData: insurances.length > 0,
  });

  useEffect(() => {
    // Nothing more is coming through an ended POA, so stop asking.
    setIsCollecting(collection.state !== "COMPLETE" && !poaInactive);
  }, [collection.state, poaInactive]);

  const queryClient = useQueryClient();
  const saveSalary = usePatchCustomerSalary(credentials, queryClient);
  const [bannerDismissed, dismissBanner] = useDismissed(
    collection.lastUpdatedAt,
  );

  const nextStep = decideNextStep({
    collectionState: collection.state,
    isChatAvailable,
    advisoryRequestSent: advisoryRequested,
  });

  const placement = collection.state === "PENDING" ? "collection" : "overview";

  useEffect(() => {
    for (const step of [
      nextStep.advisory ? "advisory" : null,
      nextStep.sam ? "sam" : null,
    ].filter(Boolean)) {
      trackEvent("pension_app_poa_overview_next_step_shown", {
        step: step as string,
        placement,
        reason: nextStep.reason,
      });
    }
  }, [nextStep.advisory, nextStep.sam, nextStep.reason, placement]);

  const isLoading =
    customerQuery.isLoading || poaQuery.isLoading || calculationQuery.isLoading;
  // Mirrors the render branches below, so the event names the screen actually shown.
  const view = isLoading
    ? null
    : noPensionData
      ? hasPublicPensionData
        ? "no_pension_data_public_only"
        : "no_pension_data"
      : poaInactive
        ? "poa_inactive"
        : collection.state === "PENDING"
          ? "no_data"
          : collection.state === "PARTIAL" && insurances.length === 0
            ? "public_only"
            : collection.state.toLowerCase();
  const forecastBasis = forecast?.basis ?? "NONE";

  useEffect(() => {
    if (!view) return;
    trackEvent("pension_app_poa_overview_viewed", {
      view,
      forecast_basis: forecastBasis,
      has_income: String(hasIncome),
    });
  }, [view, forecastBasis, hasIncome]);

  const cards = buildActionCards({
    intl,
    onAddIncome: () => setSheet("income"),
    onGetAdvice: nextStep.advisory
      ? () => {
          trackEvent("pension_app_poa_overview_next_step_clicked", {
            step: "advisory",
            placement,
            reason: nextStep.reason,
          });
          setContactOpen(true);
        }
      : undefined,
    onAskSam: nextStep.sam
      ? () => {
          trackEvent("pension_app_poa_overview_next_step_clicked", {
            step: "sam",
            placement,
            reason: nextStep.reason,
          });
          askSam();
        }
      : undefined,
    onSeeDetails: () => setSheet("handoff"),
    hasIncome,
    // Not wired: the design shows this card only to a customer who has met an advisor
    // (hasAgreedAdvice on the advice service), but such a customer never reaches this
    // screen today. Open for product: should they see the overview with this card?
    isExistingCustomer: false,
  });

  const patchAppData = usePatchCustomerAppData(credentials, queryClient);

  const showToast = (message: string) =>
    notifications.show({
      message,
      autoClose: 4000,
      radius: 16,
      color: "var(--wallet-color-masterbrand-brand-green)",
      icon: <IconCheck size={14} stroke={3} aria-hidden="true" />,
      styles: TOAST_STYLES,
      closeButtonProps: {
        "aria-label": intl.formatMessage({
          id: "app.poaOnb.addIncome.dismiss",
          defaultMessage: "Stäng",
        }),
      },
    });

  const sheets = (
    <>
      <IncomeSheet
        opened={sheet === "income"}
        onClose={() => {
          // Otherwise the previous failure is still showing the next time the sheet opens.
          saveSalary.reset();
          closeSheet();
        }}
        // The forecast can report an income while the stored salary is empty or 0.
        initialSalary={hasIncome && salary > 0 ? salary : null}
        onSave={(value) =>
          saveSalary.mutate(value, {
            onSuccess: () => {
              closeSheet();
              showToast(
                intl.formatMessage({
                  id: "app.poaOverview.toast.incomeSaved",
                  defaultMessage: "Din inkomst är sparad.",
                }),
              );
            },
          })
        }
        isSaving={saveSalary.isPending}
        isError={saveSalary.isError}
      />
      <ForecastBasisSheet
        opened={sheet === "forecast"}
        onClose={closeSheet}
        // One sheet at a time, so this closes the drawer as the income sheet opens
        onAddIncome={hasIncome ? undefined : () => setSheet("income")}
      />
      <PoaDetailsSheet
        opened={sheet === "poa"}
        onClose={closeSheet}
        validTo={customer?.poa?.validTo ?? null}
        giverName={[firstName, lastName].filter(Boolean).join(" ") || null}
        giverPersonalNumber={personalNumber || null}
      />
      <SamblaHandoffSheet opened={sheet === "handoff"} onClose={closeSheet} />
      <ContactDrawer
        opened={contactOpen}
        onClose={() => setContactOpen(false)}
        initialEmail={email}
        initialPhone={phoneNumber}
        onSubmit={async (contactEmail, contactPhone, topics) => {
          // Throwing keeps the drawer open for its own retry.
          await patchAppData.mutateAsync({
            user: {
              email: contactEmail,
              phone: normalizeToE164(contactPhone),
            },
            appData: {
              engagement: { ctaClickedAt: new Date().toISOString() },
              ...(topics.length > 0 && { contactTopics: topics }),
            },
          });

          setAdvisoryRequestSent(true);
          trackEvent("pension_app_poa_overview_advisory_requested", {
            placement,
          });
          setContactOpen(false);
          showToast(
            intl.formatMessage({
              id: "app.poaOverview.toast.advisorRequestSent",
              defaultMessage: "Tack! En pensionsrådgivare kontaktar dig.",
            }),
          );
        }}
      />
    </>
  );

  if (isLoading) {
    return (
      <AppWebviewLayout fillBody>
        <Center className={shared.content}>
          <LoaderView />
        </Center>
      </AppWebviewLayout>
    );
  }

  const reactivate = () => {
    trackEvent("pension_app_poa_overview_reactivate_clicked", {
      reason: latestPoa?.inactivatedReason ?? "NONE",
    });
    onReactivate?.();
  };
  // The block has passed, so a new POA may be signed again.
  const onRetry = noPensionData && !blocked ? reactivate : undefined;
  // The design keeps only the chat on the no pension found screens: the advisory
  // and income cards both promise something built on data that was never found.
  const blockedCards = cards.filter((card) => card.id === "askSam");

  if (noPensionData && !hasPublicPensionData) {
    return (
      <>
        <NoPensionFoundScreen cards={blockedCards} onRetry={onRetry} />
        {sheets}
      </>
    );
  }

  // An ended POA shows what was collected rather than a promise of more.
  if (collection.state === "PENDING" && !poaInactive) {
    return (
      <>
        <DataCollectionScreen cards={cards} />
        {sheets}
      </>
    );
  }

  const publicOnly =
    insurances.length === 0 && (collection.state === "PARTIAL" || poaInactive);
  const segments = distribution ? buildSegments(distribution, publicOnly) : [];
  // Without an income the backend can only produce a garantipension shaped number, so
  // the forecast is withheld and the income prompt carries the explanation instead.
  const expectedMonthlyPension =
    forecast?.basis === "UNAVAILABLE"
      ? null
      : (distribution?.firstMonthPension ?? null);

  const providers: ProviderRowData[] = insurances.map((insurance) => {
    const amount = Number(insurance.insuranceCapital);
    return {
      id: insurance.insuranceIdentifier,
      company: insurance.insuranceCompany,
      logotype: insurance.logotype,
      holderName: insurance.owner?.legalName,
      policyNumber: insurance.insuranceIdentifier,
      amount: Number.isFinite(amount) ? amount : null,
      // The holding's own import date is the truthful one. The POA answer date is
      // the fallback: it says when the company replied, not when the data landed.
      dataFrom:
        insurance.updatedAt ??
        collection.dateByOrgKey[orgKey(insurance.insuranceCompanyOrgNr)],
    };
  });

  // The POA answer and the money arrive from different services. A company that said
  // this customer has a pension with them but produced no holding would otherwise
  // vanish from the list, so it gets the design's warning row instead. Matched on the
  // organisation number: the two services spell company names differently.
  const shownCompanies = new Set(
    insurances.map((insurance) => orgKey(insurance.insuranceCompanyOrgNr)),
  );
  const unavailableProviders: ProviderRowData[] = collection.insuredCompanies
    .filter(({ key }) => !shownCompanies.has(key))
    .map(({ key, name, logotype }) => ({
      id: `unavailable-${key}`,
      company: name,
      logotype,
      amount: null,
      unavailable: true,
    }));

  // A blocked customer gets the explanation under "Dina pensioner" instead.
  const showInactiveBanner = poaInactive && !blocked;

  return (
    <>
      <AppWebviewLayout>
        <Stack gap={showInactiveBanner ? 32 : 48}>
          <Stack gap="sm">
            <Title
              order={1}
              fz="h2"
              lh="h2"
              ff="var(--wallet-font-family-sambla-sans)"
              ta="center"
            >
              <FormattedMessage
                id="app.poaOnb.overview.headline"
                defaultMessage="Pensionsöversikt"
              />
            </Title>
            <Text
              c="var(--wallet-color-neutrals-gray-600)"
              size="sm"
              lh={1.6}
              ta="center"
            >
              <FormattedMessage
                id="app.poaOnb.overview.description"
                defaultMessage="Här får du en samlad överblick, så att du enklare kan ha koll på ditt pensionssparande."
              />
            </Text>
          </Stack>
          <Stack gap="xl">
            {poaInactive ? (
              showInactiveBanner && (
                <InactivePoaBanner
                  dataFrom={collection.lastUpdatedAt}
                  onRenew={reactivate}
                />
              )
            ) : collection.state === "PARTIAL" ? (
              <CollectionProgressBanner
                variant="inProgress"
                items={[
                  progressItem(
                    intl,
                    intl.formatMessage({
                      id: "app.poaOverview.progressBanner.source.public",
                      defaultMessage: "Allmän pension",
                    }),
                    collection.publicPension === "AVAILABLE",
                  ),
                  progressItem(
                    intl,
                    intl.formatMessage({
                      id: "app.poaOverview.progressBanner.source.occupational",
                      defaultMessage: "Tjänste- och privatpensioner",
                    }),
                    collection.occupationalAndPrivate === "AVAILABLE",
                  ),
                ]}
              />
            ) : (
              // "Allt är hämtat" next to a footnote saying 7 of 13 companies answered would
              // be a contradiction, so the celebration is kept for a genuinely full set.
              !bannerDismissed &&
              collection.companiesTotal > 0 &&
              collection.companiesAnswered === collection.companiesTotal && (
                <CollectionProgressBanner
                  variant="complete"
                  onDismiss={dismissBanner}
                />
              )
            )}

            <SimpleGrid cols={2} spacing="lg">
              <PensionDistributionBar segments={segments} />
              <PensionFigures
                variant={publicOnly ? "publicOnly" : "full"}
                totalCapital={calculation?.totalAllocation?.sumOfAssets ?? null}
                retirementAge={calculation?.parameters?.statePensionAge ?? 68}
                expectedMonthlyPension={expectedMonthlyPension}
                guaranteedMonthlyPension={
                  distribution?.guaranteePension ?? null
                }
                affectedByRiskPercent={distribution?.affectedByRisk ?? null}
                riskLegendGroups={[
                  ...new Set(segments.map((segment) => segment.group)),
                ]}
                expectedFeesUntilRetirement={
                  forecast?.expectedFeesUntilRetirement ?? null
                }
                footnote={
                  collection.companiesTotal > 0
                    ? {
                        answered: collection.companiesAnswered,
                        total: collection.companiesTotal,
                      }
                    : null
                }
                // A forecast built on a schablon instead of the customer's real premiums
                // is marked with the asterisk and explained in the sheet. A missing income
                // gets the add income link, which opens the income sheet directly.
                forecastIsEstimate={forecast?.basis === "FALLBACK"}
                onForecastInfo={() => setSheet("forecast")}
                missingIncome={!hasIncome}
                onAddIncome={() => setSheet("income")}
                publicComponents={{
                  premiepension: distribution?.PPM ?? null,
                  inkomstpension: distribution?.I ?? null,
                  garantipension: distribution?.G ?? null,
                }}
              />
            </SimpleGrid>

            <ProviderList
              providers={[...providers, ...unavailableProviders]}
              loading={
                collection.occupationalAndPrivate === "PENDING" && !poaInactive
              }
              emptyMessage={
                noPensionData && (
                  <Text c="var(--wallet-color-neutrals-gray-600)" size="sm">
                    <FormattedMessage
                      id="app.poaOverview.inactivated.description"
                      defaultMessage="Vi kunde inte hitta några privat- eller tjänstepensioner via din informationsfullmakt. Därför har fullmakten inaktiverats."
                    />
                  </Text>
                )
              }
            />

            <ActionCarousel
              heading={
                noPensionData
                  ? intl.formatMessage({
                      id: "app.poaOverview.inactivated.moreActions.headline",
                      defaultMessage: "Mer om din pension",
                    })
                  : intl.formatMessage({
                      id: "app.poaOnb.waiting.moreActions.headline",
                      defaultMessage: "Gör mer med din pension",
                    })
              }
              cards={noPensionData ? blockedCards : cards}
            />

            <Text
              size="sm"
              fw={300}
              ta="center"
              c="var(--wallet-color-neutrals-gray-600)"
            >
              <FormattedMessage
                id="app.poaOverview.POAinfo"
                defaultMessage="Dina pensionsuppgifter hämtas via en informationsfullmakt."
              />{" "}
              <Anchor
                inherit
                component="button"
                type="button"
                c="black"
                underline="always"
                onClick={() => setSheet("poa")}
              >
                <FormattedMessage
                  id="app.poaOverview.poaFooter.readMore"
                  defaultMessage="Läs mer"
                />
              </Anchor>
            </Text>
          </Stack>
        </Stack>
      </AppWebviewLayout>
      {sheets}
    </>
  );
}
